import { useState } from "react";
import { Button } from "@/components/ui/button";

export interface MarketAutoPullRunRow {
  id: string;
  month: string;
  status: "requested" | "collected" | "published" | "failed";
  error: string | null;
  firstRequestedAt?: number;
  lastRequestedAt?: number;
  completedAt?: number | null;
  attempts?: number;
  summary?: { eventId?: string; received: number; previousCount: number; published: number; alreadyPresent: number;
    slack?: { status: string; attempts: number; httpStatus?: number } } | null;
}
interface Props {
  runs: readonly MarketAutoPullRunRow[];
  currentMonth: string;
  supportsHistoricalMonths: boolean;
  enabled: boolean;
  onRetry: (month: string) => Promise<void>;
  onRunNow?: () => Promise<void>;
  onResend?: (eventId: string) => Promise<void>;
  onCheckPublication?: () => Promise<void>;
}

/** Collection controls and delivery outcomes remain independent. */
export function MarketAutoPullPanel({ runs, currentMonth, supportsHistoricalMonths, enabled, onRetry, onRunNow, onResend, onCheckPublication }: Props) {
  const [month, setMonth] = useState("");
  const [pending, setPending] = useState<"run" | "retry" | "report" | "resend" | null>(null);
  const [message, setMessage] = useState("");
  const selected = runs.find(run => run.month === month);
  const historicalBlocked = !!selected && selected.month !== currentMonth && !supportsHistoricalMonths;
  const canRetry = enabled && selected?.status === "failed" && !historicalBlocked && !pending;
  const currentRun = runs.find(run => run.month === currentMonth);
  const canRun = enabled && !!onRunNow && !pending && currentRun?.status !== "published" && currentRun?.status !== "failed";
  const time = (value?: number | null) => value == null ? "—" : new Date(value).toLocaleString("en-US", { timeZone: "America/Chicago", timeZoneName: "short" });
  async function runNow() {
    if (!canRun) return;
    setPending("run"); setMessage("");
    try { await onRunNow!(); setMessage(`Run checked for ${currentMonth}. Existing work is reused; publication is not yet confirmed.`); }
    catch (error) { setMessage(error instanceof Error ? error.message : "Request failed. No publication is confirmed."); }
    finally { setPending(null); }
  }
  async function retry() {
    if (!canRetry) return;
    setPending("retry"); setMessage("");
    try { await onRetry(month); setMessage(`Retry requested for ${month}. Collection is not yet verified.`); }
    catch (error) { setMessage(error instanceof Error ? error.message : "Retry failed. No publication is confirmed."); }
    finally { setPending(null); }
  }
  return (
    <section className="min-w-0 space-y-6 rounded-xl border bg-card p-4 text-card-foreground sm:p-6" aria-label="Market auto-pull">
      <header className="space-y-1">
        <h3 className="text-lg font-semibold tracking-tight">Market auto-pull</h3>
        <p className="text-sm text-muted-foreground">Manage collection requests. Published months are immutable; only failed months can be retried.</p>
      </header>
      {!enabled && <p role="status" className="rounded-lg border bg-muted/40 p-4 text-sm">Auto-pull is disabled. No schedule or live collection is enabled.</p>}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="space-y-4 rounded-lg border bg-muted/20 p-4">
          <div className="space-y-1">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Current data month</h4>
            <p className="text-xl font-semibold">{currentMonth}</p>
            <p className="text-sm text-muted-foreground">{currentRun ? `Collection status: ${currentRun.status}` : "No collection request recorded."}</p>
          </div>
          {onRunNow && <Button className="min-h-11 w-full whitespace-normal sm:w-auto" type="button" disabled={!canRun} onClick={() => void runNow()}>Run now — {currentMonth}</Button>}
          {currentRun?.status === "published" && onCheckPublication && <div className="space-y-2 border-t pt-4">
            <Button variant="outline" className="min-h-11 w-full whitespace-normal sm:w-auto" aria-describedby="published-slack-help" disabled={!enabled || !!pending} onClick={async () => {
              setPending("report"); setMessage("");
              try { await onCheckPublication(); setMessage("Already-published report queued or already recorded for this run. No market data changed."); }
              catch { setMessage("Could not queue the already-published Slack report."); }
              finally { setPending(null); }
            }}>Queue already-published Slack report</Button>
            <p id="published-slack-help" className="text-sm text-muted-foreground">Queues an already_published Slack report once per run. Repeated requests reuse that report; they do not recollect data or resend a sent report. Use Resend report if delivery failed.</p>
          </div>}
          <p className="text-xs leading-relaxed text-muted-foreground">Due on the 15th at 00:00 America/Chicago; catches up on the next external wakeup. All run times use Chicago time.</p>
        </div>
        <div className="space-y-4 rounded-lg border p-4">
          <div className="space-y-1"><h4 className="font-semibold">Retry a failed collection</h4><p className="text-sm text-muted-foreground">Choose a failed data month. Retrying does not confirm publication.</p></div>
          <label className="block space-y-2">
            <span className="text-sm font-medium">Failed data month</span>
            <select className="block min-h-11 w-full min-w-0 rounded-md border bg-background p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" value={month} disabled={!enabled || !!pending}
              onChange={event => { setMonth(event.target.value); setMessage(""); }}>
              <option value="">Select a failed month</option>
              {runs.map(run => <option key={run.id} value={run.month} disabled={run.status !== "failed"}>
                {run.month} — {run.status}
              </option>)}
            </select>
          </label>
          {historicalBlocked && <p role="alert" className="text-sm text-destructive">The source cannot provide this historical month. Use the existing PDF import fallback; live cards cannot be relabeled.</p>}
          {selected?.error && <p className="break-words text-sm">Last failure: {selected.error}</p>}
          <Button variant="outline" className="min-h-11 w-full sm:w-auto" type="button" disabled={!canRetry} onClick={() => void retry()}>
            {pending === "retry" ? "Requesting retry…" : "Retry selected month"}
          </Button>
        </div>
      </div>
      {message && <p role="status" className="rounded-lg border bg-muted/40 p-4 text-sm">{message}</p>}
      <div className="flex items-baseline justify-between gap-3 border-b pb-3"><h4 className="font-semibold">Recent runs</h4><span className="text-xs text-muted-foreground">{runs.length} recorded</span></div>
      {!runs.length && <div className="rounded-lg border border-dashed p-5 text-sm"><p className="font-medium">No recent runs to display.</p><p className="mt-1 text-muted-foreground">Collection requests and their delivery outcomes will appear here when available.</p></div>}
      <ul className="space-y-3 text-sm" aria-label="Recent market runs">
        {runs.map(run => <li key={run.id} className="min-w-0 rounded-lg border p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h5 className="font-semibold">{run.month}: <span className={run.status === "failed" ? "text-destructive" : "text-primary"}>{run.status}</span></h5>
            <p className="text-xs text-muted-foreground">Attempts: {run.attempts ?? "—"}</p>
          </div>
          {run.summary && <p className="rounded-md bg-muted/40 px-3 py-2 leading-relaxed tabular-nums">Received: {run.summary.received} · Previous: {run.summary.previousCount} · Published: {run.summary.published} · Already present: {run.summary.alreadyPresent}</p>}
          {run.summary && <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className={run.summary.slack?.status === "failed" ? "font-medium text-destructive" : "text-muted-foreground"}>Slack: {run.summary.slack?.status ?? "pending"} · {run.summary.slack?.attempts ?? 0} delivery attempts</p>
              {run.status === "published" && run.summary.slack?.status === "failed" && <p className="mt-1">Published; Slack delivery failed. Data remains published.</p>}
            </div>
            {onResend && run.summary.eventId && run.summary.slack?.status === "failed" && <Button variant="outline" disabled={!enabled || !!pending} onClick={async () => {
              setPending("resend"); setMessage("");
              try { await onResend(run.summary!.eventId!); setMessage("Report queued for resend."); }
              catch { setMessage("Report could not be queued."); }
              finally { setPending(null); }
            }}>Resend report</Button>}
          </div>}
          {run.error && <p className="break-words text-destructive">Error: {run.error}</p>}
          <details>
            <summary tabIndex={0} className="cursor-pointer rounded-sm text-xs font-medium text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Request timing and run details</summary>
            <div className="mt-3 space-y-2 break-words text-xs leading-relaxed text-muted-foreground">
              <p>First request: {time(run.firstRequestedAt)} · Latest request: {time(run.lastRequestedAt)} · Completion: {time(run.completedAt)}</p>
              <p className="font-mono">Run ID: {run.id}</p>
              {run.summary?.slack?.httpStatus != null && <p>Slack HTTP status: {run.summary.slack.httpStatus}</p>}
            </div>
          </details>
        </li>)}
      </ul>
    </section>
  );
}
